/**
 * 编辑问卷：由前后表单状态生成 qnaires.applyStructurePatch 的 ops（增量、可同批引用 newChapterIndex / newItemIndex）
 */
import { formTypeToScene } from './qnaire-api-adapter';
import { isLocalLetterOptionId } from './utils';
import type { MultipleChoiceQuestion, Question, QuestionnaireFormData, SingleChoiceQuestion } from './types';

export class StructurePatchError extends Error {
  readonly code: 'MODE_CHANGED';

  constructor(message: string) {
    super(message);
    this.name = 'StructurePatchError';
    this.code = 'MODE_CHANGED';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * 供 tRPC 入参：后端 hashIdSchema 接受 Hashid 字符串或内部 bigint（勿对 bigint 使用 String()，会得到 "1" 且无法 decode）
 */
export function idForPatch(id: unknown): string | bigint {
  if (typeof id === 'bigint') return id;
  if (typeof id === 'number' && Number.isInteger(id)) return BigInt(id);
  return String(id ?? '');
}

/** 与后端 Hashid 或章节/题目持久 id 对齐：排除本地 q-/s- 前缀与纯数字占位 */
export function isPersistedHashidLikeId(id: string | bigint | number | undefined | null): boolean {
  if (id === undefined || id === null) return false;
  if (typeof id === 'bigint' || typeof id === 'number') return true;
  const t = String(id).trim();
  if (!t || t.includes('-')) return false;
  if (/^\d+$/.test(t)) return false;
  return /^[a-zA-Z0-9]{5,}$/.test(t);
}

function isChoiceQuestion(q: Question): q is SingleChoiceQuestion | MultipleChoiceQuestion {
  return q.type === 'single_choice' || q.type === 'multiple_choice';
}

function toBackendItemType(q: SingleChoiceQuestion | MultipleChoiceQuestion): 'radio' | 'checkbox' {
  return q.type === 'single_choice' ? 'radio' : 'checkbox';
}

function optionIsCorrect(
  q: SingleChoiceQuestion | MultipleChoiceQuestion,
  optId: string | number | bigint,
  formType: QuestionnaireFormData['type']
): number {
  if (formTypeToScene(formType) !== 'quiz') return 0;
  if (q.type === 'single_choice') {
    return String(q.correctAnswer) === String(optId) ? 1 : 0;
  }
  const ca = q.correctAnswers ?? [];
  return Array.isArray(ca) && ca.some((x) => String(x) === String(optId)) ? 1 : 0;
}

function isPersistedOptionId(id: string | number | bigint | undefined): boolean {
  const t = String(id ?? '').trim();
  if (!t) return false;
  return !isLocalLetterOptionId(t);
}

type FlatQuestion = { sectionId: string | null; orderKey: number; q: Question };

function flattenQuestions(fd: QuestionnaireFormData): FlatQuestion[] {
  const out: FlatQuestion[] = [];
  if (fd.useSections) {
    fd.sections.forEach((s, si) => {
      s.questions.forEach((q, qi) => {
        out.push({ sectionId: s.id, orderKey: si * 10_000 + qi, q });
      });
    });
  } else {
    fd.questions.forEach((q, qi) => out.push({ sectionId: null, orderKey: qi, q }));
  }
  return out;
}

/** @internal 导出供单测 */
export function buildStructurePatchOps(
  before: QuestionnaireFormData,
  after: QuestionnaireFormData,
  qnaireId: string | bigint | number
): unknown[] {
  if (before.useSections !== after.useSections) {
    throw new StructurePatchError('暂不支持在保存时切换「是否分章节」，请先保持该开关不变后再试。');
  }

  const ops: unknown[] = [];
  const chapterDeleteOps: unknown[] = [];

  let newChapterIndexBySectionId = new Map<string, number>();

  if (after.useSections) {
    const beforeSec = new Map(before.sections.map((s) => [s.id, s]));
    const afterIds = new Set(after.sections.map((s) => s.id));

    const newSectionOrder: string[] = [];
    for (const s of after.sections) {
      if (!beforeSec.has(s.id)) newSectionOrder.push(s.id);
    }
    newChapterIndexBySectionId = new Map<string, number>();
    newSectionOrder.forEach((id, idx) => newChapterIndexBySectionId.set(id, idx));

    for (const s of after.sections) {
      if (!beforeSec.has(s.id)) {
        const sort = after.sections.findIndex((x) => x.id === s.id);
        ops.push({
          entity: 'chapter',
          action: 'create',
          data: {
            qnaireId: idForPatch(qnaireId),
            name: s.name || `章节 ${sort + 1}`,
            sort,
            description: s.description ?? null,
          },
        });
      }
    }

    for (const s of after.sections) {
      const prev = beforeSec.get(s.id);
      if (!prev) continue;
      const sort = after.sections.findIndex((x) => x.id === s.id);
      const name = s.name ?? '';
      const description = s.description ?? null;
      if (
        (prev.name ?? '') !== name ||
        (prev.description ?? null) !== description ||
        before.sections.findIndex((x) => x.id === s.id) !== sort
      ) {
        ops.push({
          entity: 'chapter',
          action: 'update',
          id: idForPatch(s.id),
          data: {
            name,
            sort,
            description,
          },
        });
      }
    }

    for (const s of before.sections) {
      if (!afterIds.has(s.id) && isPersistedHashidLikeId(s.id)) {
        chapterDeleteOps.push({ entity: 'chapter', action: 'delete', id: idForPatch(s.id) });
      }
    }
  }

  const beforeFlat = flattenQuestions(before);
  const afterFlat = flattenQuestions(after);
  const beforeById = new Map(beforeFlat.map((x) => [String(x.q.id), x]));
  const afterIds = new Set(afterFlat.map((x) => String(x.q.id)));

  for (const [id, row] of beforeById) {
    if (!afterIds.has(id) && isPersistedHashidLikeId(id) && isChoiceQuestion(row.q)) {
      ops.push({ entity: 'item', action: 'delete', id: idForPatch(id) });
    }
  }

  let itemCreatesInBatch = 0;
  let choiceSortSeq = 0;

  for (const { sectionId, q } of afterFlat) {
    if (!isChoiceQuestion(q)) continue;

    const prev = beforeById.get(String(q.id));
    const sort = choiceSortSeq;
    choiceSortSeq += 1;

    if (!prev) {
      if (!isPersistedHashidLikeId(q.id)) {
        const base = {
          qnaireId: idForPatch(qnaireId),
          content: q.title || '',
          type: toBackendItemType(q),
          sort,
          isRequired: q.required ? 1 : 0,
          score: q.score ?? null,
        };

        if (after.useSections && sectionId) {
          if (isPersistedHashidLikeId(sectionId)) {
            ops.push({
              entity: 'item',
              action: 'create',
              data: { ...base, qnaireChapterId: idForPatch(sectionId) },
            });
          } else {
            const idx = newChapterIndexBySectionId.get(sectionId);
            if (idx === undefined) {
              throw new Error(`无法解析新章节 ${sectionId} 的创建序号`);
            }
            ops.push({
              entity: 'item',
              action: 'create',
              data: { ...base, newChapterIndex: idx },
            });
          }
        } else {
          ops.push({
            entity: 'item',
            action: 'create',
            data: base,
          });
        }

        const newItemIndex = itemCreatesInBatch;
        itemCreatesInBatch += 1;

        const opts = q.options ?? [];
        opts.forEach((o, oi) => {
          ops.push({
            entity: 'option',
            action: 'create',
            data: {
              newItemIndex,
              content: o.label || o.value || `选项 ${oi + 1}`,
              sort: oi,
              optionValue: o.value != null ? String(o.value) : null,
              isCorrect: optionIsCorrect(q, o.id, after.type),
            },
          });
        });
      }
      continue;
    }

    // prev 存在即表示 baseline 中已有该题，必须做选项 diff；不可再用 isPersistedHashidLikeId
    // 跳过（否则 node.id 缺失时用 "1"/"2" 等占位 id 的题目永远无法生成新增选项等 ops）。
    const p = prev.q;
    if (!isChoiceQuestion(p)) continue;

    const upd: Record<string, unknown> = {};
    if ((p.title || '') !== (q.title || '')) upd.content = q.title || '';
    if (toBackendItemType(p as SingleChoiceQuestion | MultipleChoiceQuestion) !== toBackendItemType(q)) {
      upd.type = toBackendItemType(q);
    }
    if (Boolean(p.required) !== Boolean(q.required)) upd.isRequired = q.required ? 1 : 0;
    if ((p.score ?? null) !== (q.score ?? null)) upd.score = q.score ?? null;

    if (after.useSections && sectionId) {
      const prevSid = prev.sectionId;
      if (prevSid !== sectionId) {
        if (sectionId && isPersistedHashidLikeId(sectionId)) {
          upd.qnaireChapterId = idForPatch(sectionId);
        }
      }
    }

    if (Object.keys(upd).length > 0) {
      ops.push({
        entity: 'item',
        action: 'update',
        id: idForPatch(q.id),
        data: upd,
      });
    }

    const beforeOpts = new Map(
      (p as SingleChoiceQuestion | MultipleChoiceQuestion).options.map((o) => [String(o.id), o])
    );
    const afterOpts = q.options ?? [];

    for (const [oid, o] of beforeOpts) {
      if (!afterOpts.some((x) => String(x.id) === oid) && isPersistedOptionId(oid)) {
        ops.push({ entity: 'option', action: 'delete', id: idForPatch(oid) });
      }
    }

    afterOpts.forEach((o, oi) => {
      const ic = optionIsCorrect(q, o.id, after.type);
      if (!isPersistedOptionId(o.id)) {
        ops.push({
          entity: 'option',
          action: 'create',
          data: {
            qnaireItemId: idForPatch(q.id),
            content: o.label || o.value || `选项 ${oi + 1}`,
            sort: oi,
            optionValue: o.value != null ? String(o.value) : null,
            isCorrect: ic,
          },
        });
        return;
      }

      const bo = beforeOpts.get(String(o.id));
      const label = o.label || o.value || '';
      const val = o.value != null ? String(o.value) : null;
      if (
        !bo ||
        (bo.label || '') !== label ||
        (bo.value ?? '') !== (o.value ?? '') ||
        optionIsCorrect(p as SingleChoiceQuestion | MultipleChoiceQuestion, o.id, before.type) !== ic
      ) {
        ops.push({
          entity: 'option',
          action: 'update',
          id: idForPatch(o.id),
          data: {
            content: label,
            sort: oi,
            optionValue: val,
            isCorrect: ic,
          },
        });
      }
    });
  }

  ops.push(...chapterDeleteOps);
  return ops;
}

/** 供页面调用：包装错误与空 ops */
export function tryBuildStructurePatchOps(
  before: QuestionnaireFormData | null | undefined,
  after: QuestionnaireFormData,
  qnaireId: string | bigint | number
): { ok: true; ops: unknown[] } | { ok: false; message: string } {
  if (!before) return { ok: true, ops: [] };
  try {
    const ops = buildStructurePatchOps(before, after, qnaireId);
    return { ok: true, ops };
  } catch (e) {
    if (e instanceof StructurePatchError) {
      return { ok: false, message: e.message };
    }
    throw e;
  }
}
